import { useAuth } from '@/features/auth/hooks';
import { useListWorkspaces } from '@/features/workspaces/hooks';
import {
  announceCallbackComplete,
  clearAttemptedTransitionUrl,
  getAttemptedTransitionUrl,
  setAttemptedTransitionUrl,
  subscribeToRefreshAuth,
  subscribeToRefreshWorkspaces,
} from '@customerio/hydra-nav-state';
import { PermissionsProvider } from '@customerio/hydra-permissions/react';
import { Fragment, useEffect, useMemo } from 'react';
import { createSearchParams, Navigate, Outlet, useLocation } from 'react-router-dom';
import { MainNavigation } from './components/main-navigation';

import { RecordPerformanceMetric } from '@/components/record-performance-metric';
import { RocketMissionControl } from '@/components/rocket-mission-control';
import { useActiveBundle } from '@/context/active-bundle';
import { CurrentWorkspaceProvider, useCurrentWorkspace } from '@/context/current-workspace';
import { PerformanceMetric, PerformanceProvider } from '@/context/performance';
import { SidebarRoot } from '@/features/sidebar';
import { GlobalCSSVariable, useThemeContext } from '@/context/theme';
import { hydraApi } from '@/services/api';
import { authStore, hasFeatureFlag } from '@/stores/auth';
import { appContainerFadedOutStore, appContainerOutletRenderedStore } from '@/stores/root';
import { AccountState } from '@/utils/account';
import { analytics } from '@/utils/analytics';
import { useIsSettings } from '@/utils/navigation';
import { AppBundle } from '@/utils/app-bundles';
import { useSampleWorkspace } from '@/utils/sample-workspace';
import tracking from '@/utils/tracking-scripts';
import type { PermissionsUser } from '@customerio/hydra-permissions/dist/validation';
import { UserMasqPermission } from '@customerio/hydra-permissions/masq';
import { PageLoader } from '@customerio/pluma-components/react';
import { useStore } from '@nanostores/react';
import { ErrorBoundary } from 'react-error-boundary';
import { SettingsNavigation } from './components/settings-navigation';
import { TopNavigation } from './components/top-navigation';
import { SampleWorkspaceBorder } from './components/top-navigation/components/sample-workspace';
import { WorkspaceStripe } from './components/workspace-stripe';
import { sentrySetUser, setSentryFullStorySessionUrl, setSentryMasqueradingContext } from '@/utils/sentry';

let didIdentify = false;

function AuthenticatedContent() {
  const auth = useAuth();
  const workspaces = useListWorkspaces();
  const currentWorkspace = useCurrentWorkspace();
  const appContainerFadedOut = useStore(appContainerFadedOutStore);
  const appContainerOutletRendered = useStore(appContainerOutletRenderedStore);
  const themeContext = useThemeContext();
  const location = useLocation();
  const { isSettingsPage, isAnySettingsPage } = useIsSettings();
  const workspace = useCurrentWorkspace();
  const { isSampleWorkspace } = useSampleWorkspace();

  const showMainNav = !isAnySettingsPage;
  const showSettingSideNav = isAnySettingsPage;

  useEffect(() => {
    // If CDP is enabled, set the top nav height to 40px to make sure child apps
    // have the correct CSS variable values
    themeContext.setVariable(GlobalCSSVariable.TopNavHeight, '40px');
  });

  useEffect(() => {
    return subscribeToRefreshAuth(async (event) => {
      try {
        await auth.refetch();
      } finally {
        announceCallbackComplete(event.detail.id);
      }
    });
  }, []);

  useEffect(() => {
    return subscribeToRefreshWorkspaces(async (event) => {
      try {
        await workspaces.refetch();
      } finally {
        announceCallbackComplete(event.detail.id);
      }
    });
  }, []);

  useEffect(() => {
    const showTopColor = !isSettingsPage && currentWorkspace != null && currentWorkspace.color !== '';

    if (showTopColor && currentWorkspace != null) {
      themeContext.setVariable(GlobalCSSVariable.WorkspaceBorderColor, currentWorkspace.color);
      themeContext.setVariable(GlobalCSSVariable.WorkspaceBorderHeight, '12px');
    } else {
      themeContext.setVariable(GlobalCSSVariable.WorkspaceBorderHeight, '0px');
    }
  }, [currentWorkspace, isSettingsPage, auth]);

  // Additional Sentry setup once the user is authenticated
  useEffect(() => {
    if (auth.isSuccess === false || auth.data == null) {
      return;
    }

    const { account, user } = auth.data;

    if (account == null || user == null) {
      return;
    }

    sentrySetUser(account, user);
    setSentryFullStorySessionUrl();
    setSentryMasqueradingContext(account.is_masq);
  }, [auth]);

  // inject tracking scripts once the user is authenticated
  useEffect(() => {
    if (auth.isSuccess === true && auth.data != null && !didIdentify) {
      didIdentify = true;
      const { user } = auth.data;

      // identify
      void analytics?.identify(`${user?.id}`, {
        id: `${user?.id}`,
        email: user?.email,
      });
      void hydraApi.trackIdentify({
        body: {
          properties: {
            email: user?.email,
            $current_url: window.location.href,
          },
        },
      });
    }
  }, [auth]);

  useEffect(() => {
    void analytics?.page();
  }, [location]);

  useEffect(() => {
    void hydraApi.trackPage({
      body: {
        properties: {
          $current_url: window.location.href,
        },
      },
    });
  }, [location]);

  // clean tracking scripts + analytics once the component is unmounted
  useEffect(() => {
    return () => {
      tracking.cleanup();
      void analytics?.reset();
    };
  }, []);

  // Using useMemo to make sure the attempted url setting runs before rendering,
  // i.e. before the Navigate component is rendered.
  useMemo(() => {
    if (auth.isLoading === true) {
      return;
    }

    if (auth.data == null) {
      const attemptedUrl = window.location.href.replace(/\/$/, '');
      if (getAttemptedTransitionUrl() == null && attemptedUrl !== window.location.origin) {
        setAttemptedTransitionUrl(attemptedUrl);
      }
    } else {
      clearAttemptedTransitionUrl();
    }
  }, [auth]);

  if (auth.isLoading === true) {
    return <PageLoader />;
  }

  if (auth.data == null || auth.data?.account == null || auth.data?.user == null) {
    setAttemptedTransitionUrl(window.location.href);
    return <Navigate to="/login" />;
  }

  if (workspaces.isLoading === true) {
    return <PageLoader />;
  }

  const { account, user } = auth.data;

  // If we're masqing but the current user doesn't have masq permissions set, or
  // the permissions are "none", redirect to settings page (which is accessible
  // to all users who can masq)
  if (account.is_masq === true && (user.noMasqAccess === true || user.masqLevelNotSet === true) && !isAnySettingsPage) {
    return (
      <Navigate
        to={{
          pathname: '/settings',
          search: createSearchParams({ masq_account_id: String(account.id) }).toString(),
        }}
      />
    );
  }

  const isTrial = account.account_state === AccountState.Free;
  const shouldShowRocket = hasFeatureFlag('rocket_v1') && isTrial && !isSampleWorkspace(workspace?.id);

  return (
    <>
      <RecordPerformanceMetric performanceMetric={PerformanceMetric.Authenticated} />
      <WorkspaceStripe />
      <TopNavigation />
      <div style={{ display: 'flex', minHeight: 0, flex: 1 }}>
        {showMainNav ? <MainNavigation /> : null}
        {showSettingSideNav ? <SettingsNavigation /> : null}
        <div
          id="embedded-app-container"
          style={{
            display: 'flex',
            flex: 1,
            minWidth: 0,
            overflow: 'auto',
            transition: 'opacity 0.25s ease',
            opacity: appContainerFadedOut ? 0 : 1,
          }}
        >
          {appContainerOutletRendered ? <Outlet /> : null}
        </div>
        <SidebarRoot />
        {shouldShowRocket ? (
          /* The Rocket component is experimental and isolated from the rest of the app,
            So any errors that could happen within the component can be contained */
          <ErrorBoundary fallback={<Fragment />}>
            <RocketMissionControl />
          </ErrorBoundary>
        ) : null}
      </div>
      <SampleWorkspaceBorder />
    </>
  );
}

function PermissionsProviderWrapper() {
  const auth = useStore(authStore);
  const user: PermissionsUser = {
    permissions:
      auth.user != null
        ? {
            accountPermissions: auth.user?.account_permissions,
            workspacePermissions: auth.user?.workspace_permissions,
          }
        : null,
    masq_permissions: auth.user?.masq_permissions ?? UserMasqPermission.NoMasqAccess,
  };
  const workspace = useCurrentWorkspace();
  const activeApp = useActiveBundle();
  const workspaceId =
    (activeApp === AppBundle.Journeys ? workspace?.cio_workspace_id : workspace?.cdp_workspace_id) ?? null;

  return (
    <PermissionsProvider user={user} account={auth.account} workspaceId={workspaceId}>
      <PerformanceProvider>
        <AuthenticatedContent />
      </PerformanceProvider>
    </PermissionsProvider>
  );
}

export default function Authenticated() {
  return (
    <CurrentWorkspaceProvider>
      <PermissionsProviderWrapper />
    </CurrentWorkspaceProvider>
  );
}
